import PaidBreakInputs from "./PaidBreakInputs";
import { requiredRestBreakCount, restBreakDurationHours } from "./restBreaks";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getRoleType,
  getRoleName,
  text_to_num,
  get_t_str,
  generateTimeOptions,
  filterOptions,
  text_to_num_minute_precision,
} from "../../../../helpers/helpers";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import LoadingButton from "@mui/lab/LoadingButton";
import ShiftCardModalDisplay from "./ShiftCardModalDisplay";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import WarningIcon from "@mui/icons-material/Warning";
import { createFilterOptions } from "@mui/material/Autocomplete";

import { getDefaultPaidBreakStart } from "../../../../utilities/defaultPaidBreakStart";

const OPTION_SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#6d28d9",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#6d28d9",
  },
};
const OPTION_SWITCH_LABEL_SX = { marginLeft: "-6px", marginRight: 0 };

function ShiftSingleInputModal(props) {
  const paidBreakDurationHours = restBreakDurationHours(props.restBreakRegulation);
  const shiftQualifiesForSecondBreak = React.useCallback(
    (shiftLength) => {
      const threshold = props.secondBreakShiftLengthThreshold;

      if (!threshold || threshold.hours == null) {
        return false;
      }

      return threshold.inclusive
        ? shiftLength >= threshold.hours
        : shiftLength > threshold.hours;
    },
    [props.secondBreakShiftLengthThreshold]
  );

  const initialShiftInfoString = {
    shift_start: get_t_str(props.shift_info.shift_start),
    shift_end: get_t_str(props.shift_info.shift_end),
  };

  const initialBreakInfoString = {
    break_start: get_t_str(props.shift_info.break_start),
    break_end: get_t_str(
      props.shift_info.break_start + props.shift_info.break_length
    ),
  };
  const initialBreakInfoString2 = {
    break_start: get_t_str(props.shift_info.break_start_2 || 0),
    break_end: get_t_str(
      (props.shift_info.break_start_2 || 0) +
        (props.shift_info.break_length_2 || 0)
    ),
  };

  const initialBreakChecked = props.shift_info.break_length > 0;
  const initialBreakChecked2 = (props.shift_info.break_length_2 || 0) > 0;
  const initialNoteChecked = props.shift_info.note !== null;

  const [breakChecked, setBreakChecked] = useState(initialBreakChecked);
  const [breakChecked2, setBreakChecked2] = useState(initialBreakChecked2);
  const [isBreakManuallyControlled, setIsBreakManuallyControlled] =
    React.useState();

  const [noteChecked, setNoteChecked] = useState(initialNoteChecked);

  const initialPaidBreaksChecked =
    props.paidBreakInputsEnabled &&
    (props.shift_info?.paid_breaks?.length > 0);
  const [paidBreaksChecked, setPaidBreaksChecked] = useState(
    initialPaidBreaksChecked
  );
  const [isPaidBreakManuallyControlled, setIsPaidBreakManuallyControlled] =
    React.useState(false);

  const adjustPaidBreaksWithinShift = useCallback(
    (shift) => {
      if (!props.paidBreakInputsEnabled) {
        return shift.paid_breaks || [];
      }

      const shiftStart = shift.shift_start;
      const shiftEnd = shift.shift_end;
      const shiftWindow = shiftEnd - shiftStart;

      if (!Number.isFinite(shiftStart) || !Number.isFinite(shiftEnd)) {
        return shift.paid_breaks || [];
      }

      if (shiftWindow < paidBreakDurationHours) {
        return [];
      }

      const normalizedBreaks = (shift.paid_breaks || [])
        .map((paidBreak) => ({
          ...paidBreak,
          break_duration:
            paidBreak?.break_duration || paidBreakDurationHours,
          break_start:
            paidBreak?.break_start ?? shiftStart,
        }))
        .sort((a, b) => a.break_start - b.break_start);

      let occupiedHours = 0;
      const trimmedBreaks = [];
      for (const paidBreak of normalizedBreaks) {
        if (occupiedHours + paidBreak.break_duration > shiftWindow) break;
        occupiedHours += paidBreak.break_duration;
        trimmedBreaks.push(paidBreak);
      }

      let currentStart = shiftStart;

      return trimmedBreaks.map((paidBreak, index) => {
        const remainingDuration = trimmedBreaks.slice(index).reduce(
          (total, entry) => total + entry.break_duration, 0
        );
        const latestStartForCurrent = shiftEnd - remainingDuration;
        const targetStart = Math.max(paidBreak.break_start, currentStart);
        const clampedStart = Math.min(
          Math.max(targetStart, shiftStart),
          latestStartForCurrent
        );

        const break_start = Math.max(clampedStart, currentStart);
        const break_duration = paidBreak.break_duration;

        currentStart = break_start + break_duration;

        return {
          ...paidBreak,
          break_start,
          break_duration,
        };
      });
    },
    [props.paidBreakInputsEnabled, paidBreakDurationHours]
  );

  const stableShiftId = props.shift_info?.id;
  const shiftIdentity =
    stableShiftId && stableShiftId > 0
      ? `shift-${stableShiftId}`
      : `temp-${props.shift_info?.store_team_member ?? "no-stm"}-${
          props.shift_info?.date ?? "no-date"
        }-${props.index ?? "no-index"}`;
  const paidBreakCount = props.shift_info?.paid_breaks?.length || 0;

  const previousShiftIdentityRef = useRef(shiftIdentity);
  const previousPaidBreaksEnabledRef = useRef(
    props.paidBreakInputsEnabled
  );

  const [shiftInfoString, setShiftInfoString] = React.useState(
    initialShiftInfoString
  );

  const [breakInfoString, setBreakInfoString] = React.useState(
    initialBreakInfoString
  );
  const [breakInfoString2, setBreakInfoString2] = React.useState(
    initialBreakInfoString2
  );


  React.useEffect(() => {
    if (props.open) {
      // Focus and select the shift-start input when the modal opens
      // Move focus directly to the Shift End input field by using its ID
      const shiftStartInput = document.getElementById("shift-start");
      if (shiftStartInput) {
        setTimeout(() => {
          shiftStartInput.focus(); // Move focus to the Shift End input field
          shiftStartInput.select();
        }, 0); // Adding a small timeout to ensure the DOM is ready
      }
    }
  }, [props.open]); // Ensure this runs when the modal is opened

  // First effect: Handle automatic break requirement logic
  React.useEffect(() => {
    // Only run for new shifts that aren't manually controlled
    if (!isBreakManuallyControlled && !props.existingShift) {
      const shiftLength = props.shift_info.shift_end - props.shift_info.shift_start;

      const breakRequired = props.breakRequiredWhenShiftLengthExceeds !== null
        ? shiftLength > props.breakRequiredWhenShiftLengthExceeds &&
          !props.p?.has_break_waiver
        : false;

      setBreakChecked(breakRequired || initialBreakChecked);

      if (breakRequired && !initialBreakChecked) {
        let new_break_length = props.defaultBreakLengthHours;
        let new_break_start = Math.round(
          ((props.shift_info.shift_end + props.shift_info.shift_start) / 2) * 4
        ) / 4 - new_break_length / 2;

        const updatedShift = {
          ...props.shift_info,
          break_start: new_break_start,
          break_end: new_break_start + new_break_length,
          break_length: new_break_length,
        };

        props.onUpdateShift(updatedShift);
        return; // Exit early to avoid setting other states
      }

      setNoteChecked(initialNoteChecked);
    }
  }, [
    props.shift_info.shift_start,
    props.shift_info.shift_end,
    props.breakRequiredWhenShiftLengthExceeds,
    isBreakManuallyControlled,
    props.existingShift
    // Removed initialBreakChecked and initialNoteChecked from dependencies
  ]);

  // Auto-check paid breaks based on rest break labor law regulation
  React.useEffect(() => {
    // Only auto-fill for new shifts when user hasn't manually controlled paid breaks
    if (isPaidBreakManuallyControlled || props.existingShift) return;
    if (!props.paidBreakInputsEnabled || !props.restBreakRegulation) return;

    const reg = props.restBreakRegulation;
    const minimumShiftHours = reg.minimum_shift_hours;
    const triggerHours = reg.trigger_hours;
    if (!minimumShiftHours || !triggerHours) return;

    // If the unpaid break auto-add effect will also fire this render,
    // compute what break_length it would set so we can include it in our
    // onUpdateShift call and avoid overwriting it.
    const shiftLength = props.shift_info.shift_end - props.shift_info.shift_start;
    let effectiveBreakLength = props.shift_info.break_length || 0;
    let effectiveBreakStart = props.shift_info.break_start || 0;
    const unpaidBreakWillAutoAdd =
      !isBreakManuallyControlled &&
      props.breakRequiredWhenShiftLengthExceeds !== null &&
      shiftLength > props.breakRequiredWhenShiftLengthExceeds &&
      !props.p?.has_break_waiver &&
      effectiveBreakLength === 0;
    if (unpaidBreakWillAutoAdd) {
      effectiveBreakLength = props.defaultBreakLengthHours || 0.5;
      effectiveBreakStart =
        Math.round(
          ((props.shift_info.shift_end + props.shift_info.shift_start) / 2) * 4
        ) / 4 - effectiveBreakLength / 2;
    }

    const shiftDur =
      shiftLength -
      effectiveBreakLength -
      (props.shift_info.break_length_2 || 0);

    const requiredBreaks = requiredRestBreakCount(shiftDur, reg);
    if (requiredBreaks > 0) {
      const currentBreaks = props.shift_info.paid_breaks?.length || 0;

      if (currentBreaks !== requiredBreaks ||
          props.shift_info.paid_breaks?.some((entry) => entry.break_duration !== paidBreakDurationHours)) {
        // Place paid breaks at the midpoint of each worked segment,
        // so they're naturally spaced away from unpaid breaks.
        const roundToPaidBreakInterval = (time) => Math.round(time * 6) / 6;
        const shiftStart = props.shift_info.shift_start;
        const shiftEnd = props.shift_info.shift_end;

        // Build sorted list of unpaid break boundaries to split the shift
        // Use effective values which account for auto-added unpaid break
        const unpaidEdges = [];
        if (effectiveBreakLength > 0) {
          unpaidEdges.push({
            start: effectiveBreakStart,
            end: effectiveBreakStart + effectiveBreakLength,
          });
        }
        if ((props.shift_info.break_length_2 || 0) > 0) {
          unpaidEdges.push({
            start: props.shift_info.break_start_2,
            end: props.shift_info.break_start_2 + props.shift_info.break_length_2,
          });
        }
        unpaidEdges.sort((a, b) => a.start - b.start);

        // Build worked segments (gaps between unpaid breaks)
        const segments = [];
        let cursor = shiftStart;
        for (const ub of unpaidEdges) {
          if (ub.start - cursor >= paidBreakDurationHours) {
            segments.push({ start: cursor, end: ub.start });
          }
          cursor = Math.max(cursor, ub.end);
        }
        if (shiftEnd - cursor >= paidBreakDurationHours) {
          segments.push({ start: cursor, end: shiftEnd });
        }

        // Distribute paid breaks across segments proportionally to duration,
        // then place each at the midpoint of its segment
        const totalWorked = segments.reduce((s, seg) => s + (seg.end - seg.start), 0);
        const newBreaks = [];
        let breaksRemaining = requiredBreaks;

        for (let s = 0; s < segments.length && breaksRemaining > 0; s++) {
          const seg = segments[s];
          const segDur = seg.end - seg.start;
          // Proportional share, but at least 1 if breaks remain and this is the last segment
          let count = s === segments.length - 1
            ? breaksRemaining
            : Math.round((segDur / totalWorked) * requiredBreaks);
          count = Math.max(0, Math.min(count, breaksRemaining));

          for (let i = 0; i < count; i++) {
            const position = roundToPaidBreakInterval(
              seg.start + ((i + 1) * segDur) / (count + 1)
            );
            newBreaks.push({
              break_start: Math.min(position, seg.end - paidBreakDurationHours),
              break_duration: paidBreakDurationHours,
            });
          }
          breaksRemaining -= count;
        }

        newBreaks.sort((a, b) => a.break_start - b.break_start);

        setPaidBreaksChecked(true);
        const update = {
          ...props.shift_info,
          paid_breaks: newBreaks,
        };
        // If unpaid break is also being auto-added, include it so we
        // don't overwrite the unpaid break effect's onUpdateShift call
        if (unpaidBreakWillAutoAdd) {
          update.break_start = effectiveBreakStart;
          update.break_end = effectiveBreakStart + effectiveBreakLength;
          update.break_length = effectiveBreakLength;
        }
        props.onUpdateShift(update);
      }
    } else {
      // Shift is below threshold — uncheck and clear auto-added breaks
      if (paidBreaksChecked) {
        setPaidBreaksChecked(false);
        if ((props.shift_info.paid_breaks?.length || 0) > 0) {
          props.onUpdateShift({
            ...props.shift_info,
            paid_breaks: [],
          });
        }
      }
    }
  }, [
    props.shift_info.shift_start,
    props.shift_info.shift_end,
    props.shift_info.break_length,
    props.shift_info.break_length_2,
    props.shift_info.break_start,
    props.shift_info.break_start_2,
    props.paidBreakInputsEnabled,
    props.restBreakRegulation,
    paidBreakDurationHours,
    isPaidBreakManuallyControlled,
    props.existingShift,
  ]);

  // Second effect: Update display strings when shift_info changes
  React.useEffect(() => {
    const newBreakInfoString = {
      break_start: get_t_str(props.shift_info.break_start || 0),
      break_end: get_t_str(
        (props.shift_info.break_start || 0) + (props.shift_info.break_length || 0)
      ),
    };
    const newBreakInfoString2 = {
      break_start: get_t_str(props.shift_info.break_start_2 || 0),
      break_end: get_t_str(
        (props.shift_info.break_start_2 || 0) + (props.shift_info.break_length_2 || 0)
      ),
    };

    const newShiftInfoString = {
      shift_start: get_t_str(props.shift_info.shift_start),
      shift_end: get_t_str(props.shift_info.shift_end),
    };

    // Update break checked status
    setBreakChecked((props.shift_info.break_length || 0) > 0);
    setBreakChecked2((props.shift_info.break_length_2 || 0) > 0);

    // Update strings with optimization
    setBreakInfoString(prev => {
      if (
        newBreakInfoString.break_start !== prev.break_start ||
        newBreakInfoString.break_end !== prev.break_end
      ) {
        return newBreakInfoString;
      }
      return prev;
    });
    setBreakInfoString2(prev => {
      if (
        newBreakInfoString2.break_start !== prev.break_start ||
        newBreakInfoString2.break_end !== prev.break_end
      ) {
        return newBreakInfoString2;
      }
      return prev;
    });

    setShiftInfoString(prev => {
      if (
        newShiftInfoString.shift_start !== prev.shift_start ||
        newShiftInfoString.shift_end !== prev.shift_end
      ) {
        return newShiftInfoString;
      }
      return prev;
    });
  }, [
    props.shift_info.break_start,
    props.shift_info.break_length,
    props.shift_info.break_start_2,
    props.shift_info.break_length_2,
    props.shift_info.shift_start,
    props.shift_info.shift_end
  ]);

  React.useEffect(() => {
    const identityChanged = shiftIdentity !== previousShiftIdentityRef.current;
    const paidBreaksEnabledChanged =
      props.paidBreakInputsEnabled !==
      previousPaidBreaksEnabledRef.current;

    if (!props.paidBreakInputsEnabled) {
      if (paidBreaksChecked) {
        setPaidBreaksChecked(false);
      }
    } else if (identityChanged || paidBreaksEnabledChanged) {
      setPaidBreaksChecked(paidBreakCount > 0);
    }

    if (identityChanged) {
      previousShiftIdentityRef.current = shiftIdentity;
    }
    if (paidBreaksEnabledChanged) {
      previousPaidBreaksEnabledRef.current = props.paidBreakInputsEnabled;
    }
  }, [
    shiftIdentity,
    props.paidBreakInputsEnabled,
    paidBreakCount,
    paidBreaksChecked,
  ]);

  const shiftData = {
    info: props.shift_info,
  };
  const shiftLength = shiftData.info.shift_end - shiftData.info.shift_start;
  const shiftQualifiesForConfiguredSecondBreak =
    shiftQualifiesForSecondBreak(shiftLength);

  const showSecondBreakOption =
    shiftQualifiesForConfiguredSecondBreak ||
    breakChecked2 ||
    (shiftData.info.break_length_2 || 0) > 0;
  const showBusinessDeclineOption = props.businessDeclineShiftOptionEnabled;
  const showBreakOption = true;
  const showPaidBreakOption = props.paidBreakInputsEnabled;
  const showNoteOption = true;
  const hasOptionAfterBusinessDecline =
    showBreakOption || showPaidBreakOption || showNoteOption;
  const hasOptionAfterBreak = !showPaidBreakOption && showNoteOption;
  const hasOptionAfterPaidBreak = showNoteOption;

  const paidBreakValidation = props.validatePaidBreaks
    ? props.validatePaidBreaks(shiftData.info)
    : { valid: true, message: "" };
  const unpaidBreakValidation = props.validateUnpaidBreaks
    ? props.validateUnpaidBreaks(shiftData.info)
    : { valid: true, message: "" };

  const paidBreaks = props.paidBreakInputsEnabled
    ? shiftData.info.paid_breaks || []
    : [];

  const canAddPaidBreak =
    props.paidBreakInputsEnabled &&
    getDefaultPaidBreakStart(shiftData.info, paidBreaks, paidBreakDurationHours) !== null;

  React.useEffect(() => {
    if (props.existingShift || shiftQualifiesForConfiguredSecondBreak) {
      return;
    }

    if (!breakChecked2 && (props.shift_info.break_length_2 || 0) <= 0) {
      return;
    }

    const updatedShift = {
      ...props.shift_info,
      break_start_2: 0,
      break_end_2: 0,
      break_length_2: 0,
    };

    setBreakChecked2(false);
    setBreakInfoString2({
      break_start: get_t_str(0),
      break_end: get_t_str(0),
    });
    props.onUpdateShift(updatedShift);
  }, [
    breakChecked2,
    props.existingShift,
    props.onUpdateShift,
    props.shift_info,
    props.shift_info.break_length_2,
    shiftQualifiesForConfiguredSecondBreak,
  ]);

  const roundToQuarter = (value) => Math.round(value * 4) / 4;

  const getDefaultBreakStart = (length, ratio, minimumStart = null) => {
    const shiftStart = shiftData.info.shift_start;
    const shiftEnd = shiftData.info.shift_end;
    const shiftLength = shiftEnd - shiftStart;
    const targetCenter = shiftStart + shiftLength * ratio;
    const targetStart = roundToQuarter(targetCenter - length / 2);
    const lowerBound =
      minimumStart !== null ? Math.max(shiftStart, minimumStart) : shiftStart;
    const upperBound = shiftEnd - length;

    return Math.min(Math.max(targetStart, lowerBound), upperBound);
  };

  function handleRoleChange(e) {
    const { value, name } = e.target;
    const updatedShift = {
      ...shiftData.info,
      [name]: value,
      role_type: getRoleType(value, props.role_names),
    };
    props.onUpdateShift(updatedShift);
  }

  function handleStationChange(e) {
    const { value, name } = e.target;
    const updatedShift = {
      ...shiftData.info,
      [name]: value,
      ["station"]: generateStationDict(shiftData.info.role_id, value),
    };
    // console.log(updatedShift);
    // console.log(name);
    // console.log(value);
    props.onUpdateShift(updatedShift);
  }

  function handleShiftInfo(e) {
    const { value, name } = e.target;
    const updatedShift = {
      ...shiftData.info,
      [name]: text_to_num(value),
    };

    if (name === "shift_start" || name === "shift_end") {
      updatedShift.paid_breaks = adjustPaidBreaksWithinShift(updatedShift);
    }
    setShiftInfoString({
      ...shiftInfoString,
      [name]: value,
    });
    props.onUpdateShift(updatedShift);
  }

  function handleBreakInfo(e, breakIndex = 1) {
    const { value, name } = e.target;
    const startField = breakIndex === 1 ? "break_start" : "break_start_2";
    const endField = breakIndex === 1 ? "break_end" : "break_end_2";
    const lengthField = breakIndex === 1 ? "break_length" : "break_length_2";
    const normalizedName =
      breakIndex === 1
        ? name
        : name === "break_start_2"
          ? "break_start"
          : name === "break_end_2"
            ? "break_end"
            : name;

    let updatedShift = {
      ...shiftData.info,
      [name]: text_to_num(value),
    };

    const fallbackLength =
      Number.isFinite(shiftData.info[lengthField]) &&
      shiftData.info[lengthField] > 0
        ? shiftData.info[lengthField]
        : props.defaultBreakLengthHours;

    if (!Number.isFinite(updatedShift[startField])) {
      updatedShift[startField] = shiftData.info[startField] || 0;
    }

    if (!Number.isFinite(updatedShift[endField])) {
      updatedShift[endField] = Number.isFinite(shiftData.info[endField])
        ? shiftData.info[endField]
        : updatedShift[startField] + fallbackLength;
    }

    if (updatedShift[startField] >= updatedShift[endField]) {
      updatedShift[endField] =
        updatedShift[startField] + props.defaultBreakLengthHours;
    }

    if (breakIndex === 1) {
      setBreakInfoString((prevBreakInfo) => ({
        ...prevBreakInfo,
        [normalizedName]: value,
      }));
    } else {
      setBreakInfoString2((prevBreakInfo) => ({
        ...prevBreakInfo,
        [normalizedName]: value,
      }));
    }

    updatedShift[lengthField] =
      updatedShift[endField] - updatedShift[startField];

    props.onUpdateShift(updatedShift);
  }

  const handlePaidBreakChange = (index, value) => {
    setIsPaidBreakManuallyControlled(true);
    const breakStart = text_to_num_minute_precision(value);
    const updatedBreaks = paidBreaks
      .map((paidBreak, paidBreakIndex) =>
        paidBreakIndex === index
          ? {
              ...paidBreak,
              break_start: breakStart,
              break_duration: paidBreak.break_duration || paidBreakDurationHours,
            }
          : paidBreak
      )
      .sort((a, b) => a.break_start - b.break_start);

    props.onUpdateShift({
      ...shiftData.info,
      paid_breaks: updatedBreaks,
    });
  };

  const handleAddPaidBreak = (autoTriggered) => {
    if (!props.paidBreakInputsEnabled) return;
    if (!autoTriggered) setIsPaidBreakManuallyControlled(true);

    const defaultStart = getDefaultPaidBreakStart(shiftData.info, paidBreaks, paidBreakDurationHours);
    if (defaultStart === null) return;

    const updatedBreaks = [...paidBreaks, {
      break_start: defaultStart,
      break_duration: paidBreakDurationHours,
    }].sort((a, b) => a.break_start - b.break_start);

    props.onUpdateShift({
      ...shiftData.info,
      paid_breaks: updatedBreaks,
    });
  };

  const handleRemovePaidBreak = (index) => {
    setIsPaidBreakManuallyControlled(true);
    const updatedBreaks = paidBreaks.filter((_, paidBreakIndex) => paidBreakIndex !== index);

    props.onUpdateShift({
      ...shiftData.info,
      paid_breaks: updatedBreaks,
    });
  };

  function handleCheckChange(e) {
    const { checked } = e.target;

    // User has manually toggled the checkbox
    setIsBreakManuallyControlled(true);

    let new_break_length = checked
      ? shiftData.info.shift_end - shiftData.info.shift_start > 6
        ? props.defaultBreakLengthHours
        : props.defaultBreakLengthHours
      : 0;
    let new_break_start = getDefaultBreakStart(new_break_length, 0.5);

    const updatedShift = {
      ...shiftData.info,
      break_start: new_break_start,
      break_end: new_break_start + new_break_length,
      break_length: new_break_length,
      break_start_2: checked ? shiftData.info.break_start_2 || 0 : 0,
      break_end_2: checked
        ? (shiftData.info.break_start_2 || 0) + (shiftData.info.break_length_2 || 0)
        : 0,
      break_length_2: checked ? shiftData.info.break_length_2 || 0 : 0,
    };

    if (!checked) {
      updatedShift.break_start_2 = 0;
      updatedShift.break_end_2 = 0;
      updatedShift.break_length_2 = 0;
      setBreakChecked2(false);
      setBreakInfoString2({
        break_start: get_t_str(0),
        break_end: get_t_str(0),
      });
    }

    setBreakInfoString({
      ...breakInfoString,
      break_start: get_t_str(new_break_start),
      break_end: get_t_str(new_break_start + new_break_length),
    });

    setBreakChecked(checked);
    props.onUpdateShift(updatedShift);
  }

  function handleSecondBreakCheckChange(e) {
    const { checked } = e.target;

    if (checked && !shiftQualifiesForSecondBreak(shiftLength)) {
      return;
    }

    const newBreakLength = checked ? props.defaultBreakLengthHours : 0;
    const firstBreakEnd =
      (shiftData.info.break_start || shiftData.info.shift_start) +
      (shiftData.info.break_length || 0);
    const newBreakStart = checked
      ? getDefaultBreakStart(newBreakLength, 0.75, firstBreakEnd)
      : 0;

    const updatedShift = {
      ...shiftData.info,
      break_start_2: newBreakStart,
      break_end_2: newBreakStart + newBreakLength,
      break_length_2: newBreakLength,
    };

    setBreakInfoString2({
      break_start: get_t_str(newBreakStart),
      break_end: get_t_str(newBreakStart + newBreakLength),
    });
    setBreakChecked2(checked);
    props.onUpdateShift(updatedShift);
  }

  function handleNoteCheckChange(e) {
    const { checked } = e.target;
    setNoteChecked(checked);
    if (!checked) {
      const updatedShift = {
        ...shiftData.info,
        ["note"]: null,
      };
      props.onUpdateShift(updatedShift);
    }
  }

  function handleBusinessDeclineCheckChange(e) {
    const { checked } = e.target;
    props.onUpdateShift({
      ...shiftData.info,
      business_decline: checked,
    });
  }

  function handleNoteChange(e) {
    const { value, name } = e.target;
    const updatedShift = {
      ...shiftData.info,
      ["note"]: value,
    };
    props.onUpdateShift(updatedShift);
  }

  function handlePaidBreaksCheckChange(e) {
    const { checked } = e.target;
    setIsPaidBreakManuallyControlled(true);
    setPaidBreaksChecked(checked);

    if (checked && paidBreaks.length === 0) {
      // Automatically add first paid break when checkbox is checked
      handleAddPaidBreak();
    } else if (!checked) {
      // Remove all paid breaks when unchecked
      props.onUpdateShift({
        ...shiftData.info,
        paid_breaks: [],
      });
    }
  }

  function generateBreakOptions(start, end) {
    const options = [];
    let currentTime = start;

    while (currentTime <= end) {
      options.push(get_t_str(currentTime));
      currentTime += 0.25;
    }

    return options;
  }


  function generateStationOptions(selectedRoleId) {
    const selectedRoleObject = props.role_names.find(
      (role) => role.id === selectedRoleId
    );
    return selectedRoleObject
      ? selectedRoleObject.station_options.map((station) => (
          <MenuItem key={station.station_id} value={station.station_id}>
            {station.station_name}
          </MenuItem>
        ))
      : [];
  }

  function generateStationDict(selectedRoleId, selectedStationId) {
    const selectedRoleObject = props.role_names.find(
      (role) => role.id === selectedRoleId
    );
    if (selectedRoleObject.station_options) {
      let station_dict = selectedRoleObject.station_options.find(
        (station) => station.station_id === selectedStationId
      );
      if (station_dict) {
        station_dict["id"] = station_dict["station_id"];
        station_dict["name"] = station_dict["station_name"];
        return station_dict;
      }
    }
    return null;
  }

  // console.log("SHIFT EXCEEDS", props.breakRequiredWhenShiftLengthExceeds)

  const scopedRoleInfo = Array.isArray(
    props.p?.schedule_available_role_info
  )
    ? props.p.schedule_available_role_info
    : props.p?.available_role_info;
  const currentRoleIsMissing =
    props.existingShift &&
    Array.isArray(scopedRoleInfo) &&
    !scopedRoleInfo.some(
      (assignment) => assignment.scheduled_role === shiftData.info.role_id
    );
  const currentRole = props.role_names?.find(
    (role) => role.id === shiftData.info.role_id
  );
  const availableRoleInfo = currentRoleIsMissing
    ? [
        ...scopedRoleInfo,
        {
          scheduled_role: shiftData.info.role_id,
          scheduled_role_obj: currentRole,
        },
      ]
    : scopedRoleInfo;

  return (
    <div>
      <div className="flex flex-col">
        <Box sx={{ minWidth: 120 }}>
          <p className="text-sm text-slate-500">Role</p>
          <div className="mt-1">
            <FormControl
              fullWidth
              size="small"
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
              }}
            >
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={shiftData.info.role_id}
                name="role_id"
                onChange={handleRoleChange}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "5px",
                  height: "45px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
              >
                {availableRoleInfo && availableRoleInfo.length > 0 ? (
                  availableRoleInfo
                    .sort((a, b) => {
                      if (
                        a.scheduled_role_obj?.role_type_id ===
                        b.scheduled_role_obj?.role_type_id
                      ) {
                        return a.scheduled_role_obj?.role_name.localeCompare(
                          b.scheduled_role_obj?.role_name
                        );
                      }
                      return (
                        a.scheduled_role_obj?.role_type_id -
                        b.scheduled_role_obj?.role_type_id
                      );
                    })
                    .map((role, index) => (
                      <MenuItem key={index} value={role.scheduled_role}>
                        {getRoleName(role.scheduled_role, props.role_names)}
                      </MenuItem>
                    ))
                ) : (
                  props.role_names?.map((role, index) => (
                    <MenuItem key={index} value={role.id}>
                      {role.role_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </div>
        </Box>
        <div>
          {availableRoleInfo && availableRoleInfo.length > 0 &&
            generateStationOptions(shiftData.info.role_id).length > 0 && (
              <div className="mt-4">
                <Box sx={{ minWidth: 120 }}>
                  <p className="text-sm text-slate-500">Station</p>
                  <div className="mt-1">
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                      }}
                    >
                      <Select
                        labelId="station-select-label"
                        id="station-select"
                        value={shiftData.info.station_id}
                        name="station_id"
                        onChange={handleStationChange}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                      >
                        {generateStationOptions(shiftData.info.role_id)}
                      </Select>
                    </FormControl>
                  </div>
                </Box>
              </div>
            )}
        </div>
        <div className="w-full flex gap-2 mt-5">
          <div className="w-[132px] sm:col-span-1">
            <p className="text-sm text-slate-500">Shift start</p>
            <div className="mt-1">
              <FormControl fullWidth size="small">
                <Autocomplete
                  id="shift-start"
                  disableClearable
                  options={generateTimeOptions(
                    props.start_qhnum,
                    props.schedule_day_end_qhnum
                  )}
                  value={shiftInfoString.shift_start}
                  onChange={(event, newValue) => {
                    handleShiftInfo({
                      target: { name: "shift_start", value: newValue },
                    });
                  }}
                  filterOptions={filterOptions}
                  size="small"
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "-" ||
                      event.key === "Enter" ||
                      event.key === "Tab"
                    ) {
                      event.preventDefault(); // Prevent the default dash behavior

                      // Select the first option from the autocomplete list
                      const firstOption = filterOptions(
                        generateTimeOptions(
                          props.start_qhnum,
                          props.schedule_day_end_qhnum
                        ),
                        { inputValue: event.target.value }
                      )[0];

                      if (firstOption) {
                        // Set the first option as the selected value
                        handleShiftInfo({
                          target: { name: "shift_start", value: firstOption },
                        });

                        // Move focus directly to the Shift End input field by using its ID
                        const shiftEndInput =
                          document.getElementById("shift-end");
                        if (shiftEndInput) {
                          setTimeout(() => {
                            shiftEndInput.focus(); // Move focus to the Shift End input field
                            shiftEndInput.select();
                          }, 0); // Adding a small timeout to ensure the DOM is ready
                        }
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px !important",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                          backgroundColor: "white",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
          <div className="flex justify-center items-center text-slate-400 mt-6">
            <ArrowForwardIcon />
          </div>
          <div className="w-[132px]">
            <p className="text-sm text-slate-500">Shift end</p>
            <div className="mt-1">
              <FormControl fullWidth size="small">
                <Autocomplete
                  id="shift-end"
                  disableClearable
                  options={generateTimeOptions(
                    props.start_qhnum,
                    props.schedule_day_end_qhnum
                  )}
                  value={shiftInfoString.shift_end}
                  onChange={(event, newValue) => {
                    handleShiftInfo({
                      target: { name: "shift_end", value: newValue },
                    });
                  }}
                  filterOptions={filterOptions}
                  size="small"
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  }}
                  // Handle key down event to select the first item and move focus to next field
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && event.target.value) {
                      event.preventDefault(); // Prevent the default tab/enter behavior
                      const firstOption = filterOptions(
                        generateTimeOptions(
                          props.start_qhnum,
                          props.schedule_day_end_qhnum
                        ),
                        { inputValue: event.target.value }
                      )[0];
                      if (firstOption) {
                        handleShiftInfo({
                          target: { name: "shift_end", value: firstOption },
                        });

                        // Move focus directly to the Shift End input field by using its ID
                        const breakChecked =
                          document.getElementById("break-checked");
                        if (breakChecked) {
                          setTimeout(() => {
                            breakChecked.focus(); // Move focus to the Shift End input field
                          }, 0); // Adding a small timeout to ensure the DOM is ready
                        }
                      }
                    }
                    if (event.key === "Tab" && event.target.value) {
                      event.preventDefault(); // Prevent the default tab/enter behavior
                      const firstOption = filterOptions(
                        generateTimeOptions(
                          props.start_qhnum,
                          props.schedule_day_end_qhnum
                        ),
                        { inputValue: event.target.value }
                      )[0];
                      if (firstOption) {
                        handleShiftInfo({
                          target: { name: "shift_end", value: firstOption },
                        });

                        // Move focus directly to the Shift End input field by using its ID
                        const saveShift = document.getElementById("save-shift");
                        if (saveShift) {
                          setTimeout(() => {
                            saveShift.focus(); // Move focus to the Shift End input field
                          }, 0); // Adding a small timeout to ensure the DOM is ready
                        }
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px !important",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                          backgroundColor: "white",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
          <div className="w-16 flex flex-col justify-center items-center text-slate-400 text-xs mt-6">
            <ScheduleIcon style={{ padding: "0px 5px" }} />{" "}
            {shiftData.info.shift_end - shiftData.info.shift_start + " hrs"}
          </div>
        </div>
        {!props.overlapValidation?.valid && (
          <p className="text-xs text-rose-600 mt-2">
            {props.overlapValidation?.message}
          </p>
        )}
        {!unpaidBreakValidation?.valid && (
          <p className="text-xs text-rose-600 mt-2">
            {unpaidBreakValidation?.message}
          </p>
        )}
        {showBusinessDeclineOption && (
          <div
            className={`mt-3 pb-3 ${
              hasOptionAfterBusinessDecline ? "border-b border-slate-200" : ""
            }`}
          >
            <div className="w-full flex items-center">
              <Tooltip
                title="Hides end time from employee. Managers still see the end time."
                placement="top"
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      id="business-decline-checked"
                      name="businessDeclineChecked"
                      checked={!!shiftData.info.business_decline}
                      onChange={handleBusinessDeclineCheckChange}
                      size="small"
                      sx={{
                        color: "#6d28d9",
                        "&.Mui-checked": {
                          color: "#6d28d9",
                        },
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleBusinessDeclineCheckChange({
                            target: {
                              name: "businessDeclineChecked",
                              checked: !shiftData.info.business_decline,
                            },
                          });
                        }
                      }}
                    />
                  }
                />
              </Tooltip>
              <p className="text-slate-900 text-sm -ml-3">Until Business Decline (BD)</p>
            </div>
          </div>
        )}
        <div
          className={`${showPaidBreakOption ? "pt-3 pb-2" : "py-3"} ${
            !showBusinessDeclineOption ? "mt-3 border-t border-slate-200" : ""
          } ${
            hasOptionAfterBreak ? "border-b border-slate-200" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center w-[160px]">
              <FormControlLabel
                sx={OPTION_SWITCH_LABEL_SX}
                control={
                  <Switch
                    id="break-checked"
                    name="breakChecked"
                    checked={breakChecked}
                    onChange={handleCheckChange}
                    size="small"
                    sx={OPTION_SWITCH_SX}
                  />
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault(); // Prevent default Enter behavior
                    handleCheckChange({
                      target: { name: "breakChecked", checked: !breakChecked },
                    });
                  }
                }}
              />

              <p className="text-slate-900 text-sm ml-1">{props.paidBreakInputsEnabled ? "Add unpaid break" : "Add break"}</p>
            </div>
            {props.p?.has_break_waiver && (
              <div className="flex items-center gap-x-1">
                <div className="text-amber-600">
                  <WarningIcon
                    style={{ fontSize: "16px", marginBottom: "5px" }}
                  />
                </div>
                <p className="text-xs text-amber-600">
                  {props.p?.first_name} has signed a meal period waiver
                </p>
              </div>
            )}
          </div>
          {breakChecked && (
            <>
              <div className="w-full flex gap-2 mt-1 pb-2">
                <div className="w-[132px]">
                  <p className="text-sm text-slate-500">Break start</p>
                  <div className="mt-1">
                    <FormControl fullWidth size="small">
                      <Autocomplete
                        id="break_start"
                        name="break_start"
                        disableClearable
                        options={generateBreakOptions(
                          text_to_num(shiftInfoString.shift_start),
                          text_to_num(shiftInfoString.shift_end)
                        )}
                        value={breakInfoString.break_start}
                        onChange={(event, newValue) => {
                          handleBreakInfo({
                            target: { name: "break_start", value: newValue },
                          });
                        }}
                        filterOptions={filterOptions}
                        size="small"
                        sx={{
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                        onKeyDown={(event) => {
                          if (
                            (event.key === "Enter" ||
                              event.key === "-" ||
                              event.key === "Tab") &&
                            event.target.value
                          ) {
                            event.preventDefault();
                            const firstOption = filterOptions(
                              generateBreakOptions(
                                text_to_num(shiftInfoString.shift_start),
                                text_to_num(shiftInfoString.shift_end)
                              ),
                              { inputValue: event.target.value }
                            )[0];
                            if (firstOption) {
                              handleBreakInfo({
                                target: {
                                  name: "break_start",
                                  value: firstOption,
                                },
                              });

                              const breakEnd =
                                document.getElementById("break-end");
                              if (breakEnd) {
                                setTimeout(() => {
                                  breakEnd.focus();
                                  breakEnd.select();
                                }, 0);
                              }
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px !important",
                                height: "45px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                                backgroundColor: "white",
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                </div>
                <div className="flex justify-center items-center text-slate-400 mt-6">
                  <ArrowForwardIcon />
                </div>
                <div className="w-[132px]">
                  <p className="text-sm text-slate-500">Break end</p>
                  <div className="mt-1">
                    <FormControl fullWidth size="small">
                      <Autocomplete
                        id="break-end"
                        name="break_end"
                        disableClearable
                        options={generateBreakOptions(
                          text_to_num(shiftInfoString.shift_start),
                          text_to_num(shiftInfoString.shift_end)
                        )}
                        value={breakInfoString.break_end}
                        onChange={(event, newValue) => {
                          handleBreakInfo({
                            target: { name: "break_end", value: newValue },
                          });
                        }}
                        filterOptions={filterOptions}
                        size="small"
                        sx={{
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        }}
                        onKeyDown={(event) => {
                          if (
                            (event.key === "Enter" || event.key === "Tab") &&
                            event.target.value
                          ) {
                            event.preventDefault();
                            const firstOption = filterOptions(
                              generateBreakOptions(
                                text_to_num(shiftInfoString.shift_start),
                                text_to_num(shiftInfoString.shift_end)
                              ),
                              { inputValue: event.target.value }
                            )[0];
                            if (firstOption) {
                              handleBreakInfo({
                                target: { name: "break_end", value: firstOption },
                              });
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px !important",
                                height: "45px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                                backgroundColor: "white",
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </div>
                </div>
                <div className="w-16 flex flex-col justify-center items-center text-slate-400 text-xs mt-6">
                  <ScheduleIcon style={{ padding: "0px 5px" }} />{" "}
                  {shiftData.info.break_length + " hrs"}
                </div>
              </div>
              {showSecondBreakOption && (
                <div className="mt-2">
                  <div className="flex items-center">
                    <FormControlLabel
                      sx={OPTION_SWITCH_LABEL_SX}
                      control={
                        <Switch
                          id="break-checked-2"
                          name="breakChecked2"
                          checked={breakChecked2}
                          onChange={handleSecondBreakCheckChange}
                          size="small"
                          sx={OPTION_SWITCH_SX}
                        />
                      }
                    />
                    <p className="text-slate-900 text-sm ml-1">
                      Add second unpaid break
                    </p>
                  </div>
                  {breakChecked2 && (
                    <div className="w-full flex gap-2 mt-1 pb-2">
                    <div className="w-[132px]">
                      <p className="text-sm text-slate-500">2nd Break start</p>
                      <div className="mt-1">
                        <FormControl fullWidth size="small">
                          <Autocomplete
                            id="break-start-2"
                            name="break_start_2"
                            disableClearable
                            options={generateBreakOptions(
                              text_to_num(shiftInfoString.shift_start),
                              text_to_num(shiftInfoString.shift_end)
                            )}
                            value={breakInfoString2.break_start}
                            onChange={(event, newValue) => {
                              handleBreakInfo(
                                {
                                  target: {
                                    name: "break_start_2",
                                    value: newValue,
                                  },
                                },
                                2
                              );
                            }}
                            filterOptions={filterOptions}
                            size="small"
                            sx={{
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#6d28d9",
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px !important",
                                    height: "45px",
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#6d28d9",
                                      },
                                    backgroundColor: "white",
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </div>
                    <div className="flex justify-center items-center text-slate-400 mt-6">
                      <ArrowForwardIcon />
                    </div>
                    <div className="w-[132px]">
                      <p className="text-sm text-slate-500">2nd Break end</p>
                      <div className="mt-1">
                        <FormControl fullWidth size="small">
                          <Autocomplete
                            id="break-end-2"
                            name="break_end_2"
                            disableClearable
                            options={generateBreakOptions(
                              text_to_num(shiftInfoString.shift_start),
                              text_to_num(shiftInfoString.shift_end)
                            )}
                            value={breakInfoString2.break_end}
                            onChange={(event, newValue) => {
                              handleBreakInfo(
                                {
                                  target: {
                                    name: "break_end_2",
                                    value: newValue,
                                  },
                                },
                                2
                              );
                            }}
                            filterOptions={filterOptions}
                            size="small"
                            sx={{
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#6d28d9",
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px !important",
                                    height: "45px",
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#6d28d9",
                                      },
                                    backgroundColor: "white",
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </div>
                    <div className="w-16 flex flex-col justify-center items-center text-slate-400 text-xs mt-6">
                      <ScheduleIcon style={{ padding: "0px 5px" }} />{" "}
                      {(shiftData.info.break_length_2 || 0) + " hrs"}
                    </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      {showPaidBreakOption && (
        <PaidBreakInputs
          paidBreakDurationHours={paidBreakDurationHours}
          paidBreaksChecked={paidBreaksChecked}
          paidBreaks={paidBreaks}
          canAddPaidBreak={canAddPaidBreak}
          handlePaidBreaksCheckChange={handlePaidBreaksCheckChange}
          handleAddPaidBreak={handleAddPaidBreak}
          handlePaidBreakChange={handlePaidBreakChange}
          handleRemovePaidBreak={handleRemovePaidBreak}
          paidBreakValidation={paidBreakValidation}
          startQhnum={props.start_qhnum}
          scheduleDayEndQhnum={props.schedule_day_end_qhnum}
          hasFollowingOption={hasOptionAfterPaidBreak}
        />
      )}
    <div className="py-3">
      <div className="w-full flex items-center">
        <FormControlLabel
          sx={OPTION_SWITCH_LABEL_SX}
          control={
                <Switch
                  id="note-checked"
                  name="noteChecked"
                  checked={noteChecked}
                  onChange={handleNoteCheckChange}
                  size="small"
                  sx={OPTION_SWITCH_SX}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault(); // Prevent default Enter behavior
                      handleNoteCheckChange({
                        target: { name: "noteChecked", checked: !noteChecked },
                      });
                    }
                  }}
                />
              }
            />
            <p className="text-slate-900 text-sm ml-1">Add note</p>
          </div>
          {noteChecked && (
            <div className="w-full pb-2">
              <div className="w-full">
                <p className="text-sm text-slate-500">Note</p>
                <div className="mt-1">
                  <TextField
                    placeholder="Add a note to employee's shift card"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="note"
                    value={shiftData.info.note}
                    onChange={handleNoteChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShiftSingleInputModal;
